import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'react-feather';
import { api, DataPointValue, Question, Variable } from '../api/client';
import { useSnackbar } from '../context/SnackbarContext';

interface Props {
  questions: Question[];
}

interface QuestionLogState {
  variables: Variable[];
  lastLoggedAt: string | null;
  error: string | null;
}

type SaveState = 'idle' | 'saving' | 'saved';

const QUICK_LOG_ROW_LIMIT = 3;
const DAY_IN_MS = 86400000;
const RELATIVE_TIME_UNITS = [
  { label: 'year', days: 365 },
  { label: 'month', days: 30 },
  { label: 'week', days: 7 },
  { label: 'day', days: 1 },
];

const isFilled = (value: unknown): boolean => {
  return value !== undefined && value !== null && String(value).trim() !== '';
};

const isSameDay = (a: Date, b: Date): boolean => {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const formatLastLogged = (value: string | null): string => {
  if (!value) {
    return 'No datapoints yet';
  }

  const now = new Date();
  const loggedAt = new Date(value);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const loggedDay = new Date(loggedAt.getFullYear(), loggedAt.getMonth(), loggedAt.getDate());
  const daysAgo = Math.max(
    0,
    Math.floor((today.getTime() - loggedDay.getTime()) / DAY_IN_MS)
  );

  if (daysAgo === 0) {
    return 'Last logged today';
  }

  const unit = RELATIVE_TIME_UNITS.find((candidate) => daysAgo >= candidate.days)
    ?? RELATIVE_TIME_UNITS[RELATIVE_TIME_UNITS.length - 1];
  const count = Math.floor(daysAgo / unit.days);
  const label = count === 1 ? unit.label : `${unit.label}s`;

  return `Last logged ${count} ${label} ago`;
};

const wasLoggedToday = (value: string | null): boolean => {
  if (!value) {
    return false;
  }

  return isSameDay(new Date(value), new Date());
};

const sortVariables = (variables: Variable[]): Variable[] => {
  return [...variables].sort((a, b) => {
    if (a.dependent !== b.dependent) {
      return a.dependent ? -1 : 1;
    }
    return a.created_at.localeCompare(b.created_at);
  });
};

const QuickLogPanel: React.FC<Props> = ({ questions }) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [questionData, setQuestionData] = useState<Record<string, QuestionLogState>>({});
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});
  const [categoricalValues, setCategoricalValues] = useState<Record<string, string[]>>({});
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({});
  const [showAll, setShowAll] = useState(false);

  const questionsWithData = useMemo(
    () => questions.filter((question) => questionData[question.id]?.variables.length > 0),
    [questionData, questions]
  );

  const loggedToday = questionsWithData.filter((question) => {
    const lastLoggedAt = questionData[question.id].lastLoggedAt;
    return wasLoggedToday(lastLoggedAt);
  }).length;
  const visibleQuestions = showAll ? questions : questions.slice(0, QUICK_LOG_ROW_LIMIT);
  const hiddenQuestionCount = Math.max(questions.length - visibleQuestions.length, 0);

  useEffect(() => {
    let cancelled = false;

    const loadRows = async () => {
      if (questions.length === 0) {
        setQuestionData({});
        setLoading(false);
        return;
      }

      setLoading(true);
      const rows = await Promise.all(
        questions.map(async (question): Promise<[string, QuestionLogState]> => {
          try {
            const [variables, dataset] = await Promise.all([
              api.listVariables(question.id),
              api.listDataset(question.id, 1, 1),
            ]);
            return [
              question.id,
              {
                variables: sortVariables(variables),
                lastLoggedAt: dataset.data.datapoints[0]?.created_at ?? null,
                error: null,
              },
            ];
          } catch (err) {
            return [
              question.id,
              {
                variables: [],
                lastLoggedAt: null,
                error: err instanceof Error ? err.message : 'Failed to load quick log data',
              },
            ];
          }
        })
      );

      if (cancelled) {
        return;
      }

      const nextQuestionData = Object.fromEntries(rows);
      setQuestionData(nextQuestionData);

      const categoricalVariables = rows.flatMap(([, row]) =>
        row.variables.filter((variable) => variable.type === 'categorical')
      );
      const categoryRows = await Promise.all(
        categoricalVariables.map(async (variable): Promise<[string, string[]]> => {
          try {
            return [variable.id, await api.listCategoricalValues(variable.id)];
          } catch {
            return [variable.id, []];
          }
        })
      );

      if (!cancelled) {
        setCategoricalValues(Object.fromEntries(categoryRows));
        setLoading(false);
      }
    };

    void loadRows();

    return () => {
      cancelled = true;
    };
  }, [questions]);

  const handleChange = (questionId: string, variableId: string, value: string) => {
    setValues((current) => ({
      ...current,
      [questionId]: {
        ...(current[questionId] ?? {}),
        [variableId]: value,
      },
    }));
    setSaveStates((current) => ({ ...current, [questionId]: 'idle' }));
  };

  const handleSave = async (question: Question) => {
    const rowData = questionData[question.id];
    if (!rowData) {
      return;
    }

    const rowValues = values[question.id] ?? {};
    if (!rowData.variables.some((variable) => isFilled(rowValues[variable.id]))) {
      showSnackbar('Enter at least one value before saving.', { variant: 'error' });
      return;
    }

    const convertedValues: Record<string, DataPointValue> = {};
    for (const variable of rowData.variables) {
      const rawValue = rowValues[variable.id];
      if (!isFilled(rawValue)) {
        convertedValues[variable.id] = { value: null };
      } else if (variable.type === 'boolean') {
        convertedValues[variable.id] = { value: rawValue === 'true' };
      } else if (variable.type === 'numeric') {
        const numericValue = Number(rawValue);
        if (!Number.isFinite(numericValue)) {
          showSnackbar(`${variable.name} must be a number.`, { variant: 'error' });
          return;
        }
        convertedValues[variable.id] = { value: numericValue };
      } else {
        convertedValues[variable.id] = { value: rawValue.trim() };
      }
    }

    try {
      setSaveStates((current) => ({ ...current, [question.id]: 'saving' }));
      const recordedAt = new Date().toISOString();
      await api.createDataPoint(question.id, convertedValues, recordedAt);
      setValues((current) => ({ ...current, [question.id]: {} }));
      setSaveStates((current) => ({ ...current, [question.id]: 'saved' }));
      showSnackbar('Datapoint added.', { variant: 'success' });
      setQuestionData((current) => ({
        ...current,
        [question.id]: {
          ...current[question.id],
          lastLoggedAt: recordedAt,
        },
      }));

      const newCategories = rowData.variables
        .filter((variable) => variable.type === 'categorical')
        .filter((variable) => convertedValues[variable.id].value !== null)
        .map((variable) => [variable.id, String(convertedValues[variable.id].value)] as [string, string]);
      if (newCategories.length > 0) {
        setCategoricalValues((current) => {
          const next = { ...current };
          for (const [variableId, value] of newCategories) {
            next[variableId] = Array.from(new Set([...(next[variableId] ?? []), value])).sort();
          }
          return next;
        });
      }
    } catch (err) {
      setSaveStates((current) => ({ ...current, [question.id]: 'idle' }));
      showSnackbar(err instanceof Error ? err.message : 'Failed to save datapoint', { variant: 'error' });
    }
  };

  if (questions.length === 0) {
    return null;
  }

  return (
    <section className="quick-log-panel" aria-labelledby="quick-log-title">
      <div className="quick-log-header">
        <div className="quick-log-heading">
          <h2 id="quick-log-title">Log Today</h2>
          {loading && <span>Loading questions...</span>}
        </div>
        <div className="quick-log-header-actions">
          <span className="quick-log-today">{loggedToday} logged today</span>
        </div>
      </div>

      <div className="quick-log-rows">
        {visibleQuestions.map((question) => {
          const rowData = questionData[question.id];
          const rowValues = values[question.id] ?? {};
          const saveState = saveStates[question.id] ?? 'idle';
          const canSave = !!rowData?.variables.length && rowData.variables.some((variable) => isFilled(rowValues[variable.id]));

          return (
            <form
              key={question.id}
              className="quick-log-row"
              onSubmit={(event) => {
                event.preventDefault();
                void handleSave(question);
              }}
            >
              <div className="quick-log-question">
                <Link to={`/questions/${question.id}`}>{question.title}</Link>
                <span>{formatLastLogged(rowData?.lastLoggedAt ?? null)}</span>
                {rowData?.error && <span className="quick-log-row-error">{rowData.error}</span>}
              </div>

              <div className="quick-log-fields-shell">
                <div className="quick-log-fields" tabIndex={0} aria-label={`${question.title} variables`}>
                  {!rowData && <span className="quick-log-muted">Loading variables...</span>}
                  {rowData?.variables.length === 0 && !rowData.error && (
                    <span className="quick-log-muted">Add variables to start logging here.</span>
                  )}
                  {rowData?.variables.map((variable) => (
                    variable.type === 'boolean' ? (
                      <div key={variable.id} className="quick-log-field quick-log-field-boolean">
                        <span>{variable.name}</span>
                        <span className="quick-log-toggle" role="group" aria-label={variable.name}>
                          <button
                            type="button"
                            className={rowValues[variable.id] === 'true' ? 'active' : ''}
                            onClick={() => handleChange(question.id, variable.id, 'true')}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className={rowValues[variable.id] === 'false' ? 'active' : ''}
                            onClick={() => handleChange(question.id, variable.id, 'false')}
                          >
                            No
                          </button>
                        </span>
                      </div>
                    ) : (
                      <label key={variable.id} className={`quick-log-field quick-log-field-${variable.type}`}>
                        <span>{variable.name}</span>
                        {variable.type === 'numeric' ? (
                          <input
                            type="number"
                            step="any"
                            value={rowValues[variable.id] ?? ''}
                            onChange={(event) => handleChange(question.id, variable.id, event.target.value)}
                            placeholder="-"
                            aria-label={variable.name}
                          />
                        ) : (
                          <>
                            <input
                              type="text"
                              value={rowValues[variable.id] ?? ''}
                              onChange={(event) => handleChange(question.id, variable.id, event.target.value)}
                              placeholder="Select or type"
                              list={categoricalValues[variable.id]?.length ? `${variable.id}-quick-values` : undefined}
                              aria-label={variable.name}
                            />
                            {categoricalValues[variable.id]?.length > 0 && (
                              <datalist id={`${variable.id}-quick-values`}>
                                {categoricalValues[variable.id].map((value) => (
                                  <option key={value} value={value} />
                                ))}
                              </datalist>
                            )}
                          </>
                        )}
                      </label>
                    )
                  ))}
                </div>
              </div>

              <div className="quick-log-actions">
                <button
                  type="submit"
                  className="quick-log-save"
                  disabled={!canSave || saveState === 'saving'}
                >
                  <Plus size={14} />
                  {saveState === 'saving' ? 'Saving...' : saveState === 'saved' ? 'Added' : 'Add'}
                </button>
              </div>
            </form>
          );
        })}
      </div>
      {questions.length > QUICK_LOG_ROW_LIMIT && (
        <button
          type="button"
          className="quick-log-more"
          onClick={() => setShowAll((current) => !current)}
        >
          {showAll ? 'Show less' : `Show ${hiddenQuestionCount} more`}
        </button>
      )}
    </section>
  );
};

export default QuickLogPanel;
